import { useEffect, useState } from 'react';

import { useCurrentTier } from '@/hooks/useCurrentTier';
import {
  useSessionUpdateMutation,
  useTTLSettings,
  useUpsertOrgTTLSettings,
  useUpsertTTLSettings,
} from '@/hooks/useSwr';
import { SessionSchema, TraceTTLTier } from '@/types/schema';

type Params = {
  project?: SessionSchema;
};
export const useConfigureProjectTTLSettings = ({ project }: Params) => {
  const [ttl, setTTL] = useState<TraceTTLTier | null>(null);

  useEffect(() => {
    if (project?.trace_tier) {
      setTTL(project.trace_tier);
    }
  }, [project?.trace_tier]);

  const editProject = useSessionUpdateMutation(project?.id || '');

  const handleProjectTTLChange = async (mTTL: TraceTTLTier | null) => {
    if (mTTL) {
      await editProject.trigger({
        json: {
          trace_tier: mTTL,
        },
      });
    }
  };

  const reset = () => {
    setTTL(project?.trace_tier || null);
  };

  return {
    ttl,
    setTTL,
    handleProjectTTLChange,
    reset,
  };
};

export const useConfigureOrgTTLSettings = () => {
  const { isLegacy, isEnterprise, isLoading } = useCurrentTier();

  const ttlSettingQuery = useTTLSettings({
    skip: isLegacy && !isEnterprise,
  });

  const upsertTTLSettings = useUpsertTTLSettings();
  const upsertOrgTTLSettings = useUpsertOrgTTLSettings();

  const handleTTLChange = async (
    workspaceId: string,
    ttl: TraceTTLTier,
    applyToAllProjects: boolean
  ) => {
    await upsertTTLSettings.trigger({
      json: {
        tenant_id: workspaceId,
        default_trace_tier: ttl,
        apply_to_all_projects: applyToAllProjects,
      },
    });
  };

  const handleOrgTTLChange = async (
    ttl: TraceTTLTier,
    applyToAllProjects: boolean
  ) => {
    await upsertOrgTTLSettings.trigger({
      json: {
        default_trace_tier: ttl,
        apply_to_all_projects: applyToAllProjects,
      },
    });
  };

  return {
    isLoading: ttlSettingQuery.isLoading || isLoading,
    allTTLSettings: ttlSettingQuery.data,
    handleTTLChange,
    handleOrgTTLChange,
    isMutating: upsertTTLSettings.isMutating || upsertOrgTTLSettings.isMutating,
  };
};
